import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { formatDateBR, type ExpiryStatus, type StockItemView } from '@almox/shared';

/* ---------------------------------------------------------------- toasts -- */

type ToastKind = 'ok' | 'error' | 'info';
interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

const ToastContext = createContext<(message: string, kind?: ToastKind) => void>(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, kind }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 3200);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.kind}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/* ----------------------------------------------------------------- modal -- */

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- badges -- */

const EXPIRY_CLASS: Record<ExpiryStatus, string> = {
  ok: 'badge badge-ok',
  warning: 'badge badge-warn',
  expired: 'badge badge-danger',
  unknown: 'badge',
};

/** Validade com cor e o "~" das datas estimadas (regra 3). */
export function ExpiryBadge({ item }: { item: StockItemView }) {
  if (!item.expiresAt) return <span className="badge">sem validade</span>;
  const prefix = item.expiryEstimated ? '~ ' : '';
  const days = item.daysUntilExpiry ?? 0;
  const label =
    days < 0
      ? `venceu em ${formatDateBR(item.expiresAt)}`
      : days === 0
        ? `vence hoje (${formatDateBR(item.expiresAt)})`
        : `vence em ${formatDateBR(item.expiresAt)}`;
  return (
    <span
      className={EXPIRY_CLASS[item.expiryStatus]}
      title={item.expiryEstimated ? 'Data estimada pela validade padrao' : 'Data informada'}
    >
      {prefix}
      {label}
    </span>
  );
}

/* ----------------------------------------------------------------- icones -- */

const STORAGE_EMOJI: Record<string, string> = {
  fridge: '🧊',
  freezer: '❄️',
  cabinet: '🚪',
  pantry: '🥫',
  bath: '🛁',
  laundry: '🧺',
  box: '📦',
  shelf: '🗄️',
  garage: '🚗',
  pet: '🐾',
};

export function storageEmoji(icon: string): string {
  return STORAGE_EMOJI[icon] ?? '📦';
}

export const STORAGE_ICON_OPTIONS = Object.keys(STORAGE_EMOJI);

/* ------------------------------------------------------------------ dados -- */

export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[],
): { data: T | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loader()
      .then((value) => {
        if (!active) return;
        setData(value);
        setError(null);
      })
      .catch((cause: Error) => active && setError(cause.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return useMemo(() => ({ data, loading, error, reload }), [data, loading, error, reload]);
}

export function EmptyState({ icon, title, hint }: { icon: string; title: string; hint?: string }) {
  return (
    <div className="empty">
      <div className="icon">{icon}</div>
      <p style={{ fontWeight: 650, color: 'var(--text)' }}>{title}</p>
      {hint ? <p className="small">{hint}</p> : null}
    </div>
  );
}

/**
 * Lista de categorias para o autocompletar. O `list="categorias"` dos campos
 * apontava para um datalist que nunca existiu — nada completava.
 */
export function CategoryDatalist({ categories }: { categories: string[] }) {
  return (
    <datalist id="categorias">
      {categories.map((categoria) => (
        <option key={categoria} value={categoria} />
      ))}
    </datalist>
  );
}

/** Marcas ja cadastradas, para nao virar "Italac", "italac" e "ITALAC". */
export function BrandDatalist({ brands }: { brands: string[] }) {
  return (
    <datalist id="marcas">
      {brands.map((marca) => (
        <option key={marca} value={marca} />
      ))}
    </datalist>
  );
}
